import React, { useEffect, useState, useRef } from "react";
import "./AnimatedPhotoWall.css";
import "./FloatingAcheBalloon.css";
import { getImageUrl } from "../config/assets";

export interface FloatingAcheBalloonProps {
  id: string;
  // Configurações de movimento
  speed?: number; // pixels por segundo (padrão: 6)
  minDistance?: number; // distância mínima para considerar que chegou no alvo (padrão: 2)
  // Configurações de posição inicial
  initialPosition?: { left: number; top: number };
  // Configurações de área de movimento
  movementBounds?: {
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
    // Área proibida (ex: logo central)
    forbiddenArea?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    // Área proibida adicional (ex: logo canto)
    forbiddenAreaAche?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
    // Área proibida do texto central
    forbiddenAreaText?: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
  };
  // Tamanho do balão
  sizePercentage?: number; // Tamanho em % da altura da tela (padrão: 5)
  // Configurações visuais
  className?: string;
  style?: React.CSSProperties;
  // Controle de transição suave
  shouldEnter?: boolean; // Se deve entrar da tela (padrão: false)
  shouldExit?: boolean; // Se deve sair da tela
  onExitComplete?: () => void; // Callback quando sair completamente
}

const getRandomTarget = (bounds?: FloatingAcheBalloonProps['movementBounds'], balloonSize: number = 50) => {
  // Garantir que os elementos fiquem completamente dentro da área visível
  const balloonWidth = balloonSize;
  const balloonHeight = balloonSize;
  
  const minX = bounds?.minX || 0;
  const maxX = bounds?.maxX || (window.innerWidth - balloonWidth);
  const minY = bounds?.minY || 0;
  const maxY = bounds?.maxY || (window.innerHeight - balloonHeight);

  // Garantir que os limites não sejam negativos e não ultrapassem a tela
  const safeMinX = Math.max(0, minX);
  const safeMaxX = Math.min(window.innerWidth - balloonWidth, maxX);
  const safeMinY = Math.max(0, minY);
  const safeMaxY = Math.min(window.innerHeight - balloonHeight, maxY);

  // Função para verificar se uma posição está na área proibida
  const isInForbiddenArea = (x: number, y: number) => {
    if (!bounds?.forbiddenArea && !bounds?.forbiddenAreaAche && !bounds?.forbiddenAreaText) return false;
    
    // Verificar área proibida principal (logo ASCME central)
    if (bounds?.forbiddenArea) {
      const forbidden = bounds.forbiddenArea;
      const inMainForbiddenArea = (
        x + balloonWidth > forbidden.left &&
        x < forbidden.right &&
        y + balloonHeight > forbidden.top &&
        y < forbidden.bottom
      );
      if (inMainForbiddenArea) return true;
    }
    
    // Verificar área proibida adicional (logo ACHE canto)
    if (bounds?.forbiddenAreaAche) {
      const forbiddenAche = bounds.forbiddenAreaAche;
      const inAcheForbiddenArea = (
        x + balloonWidth > forbiddenAche.left &&
        x < forbiddenAche.right &&
        y + balloonHeight > forbiddenAche.top &&
        y < forbiddenAche.bottom
      );
      if (inAcheForbiddenArea) return true;
    }
    
    // Verificar área proibida do texto central
    if (bounds?.forbiddenAreaText) {
      const forbiddenText = bounds.forbiddenAreaText;
      const inTextForbiddenArea = (
        x + balloonWidth > forbiddenText.left &&
        x < forbiddenText.right &&
        y + balloonHeight > forbiddenText.top &&
        y < forbiddenText.bottom
      );
      if (inTextForbiddenArea) return true;
    }
    
    return false;
  };

  // Tentar encontrar uma posição válida
  let attempts = 0;
  let left, top;
  
  do {
    left = Math.max(safeMinX, Math.min(safeMaxX, Math.random() * (safeMaxX - safeMinX) + safeMinX));
    top = Math.max(safeMinY, Math.min(safeMaxY, Math.random() * (safeMaxY - safeMinY) + safeMinY));
    attempts++;
  } while (isInForbiddenArea(left, top) && attempts < 50);

  // Se não conseguir evitar a área proibida, escolher uma área segura
  if (attempts >= 50 && (bounds?.forbiddenArea || bounds?.forbiddenAreaAche)) {
    const forbidden = bounds?.forbiddenArea;
    const forbiddenAche = bounds?.forbiddenAreaAche;
    
    // Dividir tela em áreas seguras considerando ambas as áreas proibidas
    const areas = [];
    
    // Área esquerda (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: safeMinX, 
        maxX: Math.max(safeMinX, forbidden.left - balloonWidth), 
        minY: safeMinY, 
        maxY: safeMaxY 
      });
    }
    
    // Área direita (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: Math.min(safeMaxX, forbidden.right), 
        maxX: safeMaxX, 
        minY: safeMinY, 
        maxY: safeMaxY 
      });
    }
    
    // Área superior (evita ambas as logos)
    let topMaxY = safeMaxY;
    if (forbidden) topMaxY = Math.min(topMaxY, forbidden.top - balloonHeight);
    if (forbiddenAche) topMaxY = Math.min(topMaxY, forbiddenAche.top - balloonHeight);
    areas.push({ 
      minX: safeMinX, 
      maxX: safeMaxX, 
      minY: safeMinY, 
      maxY: Math.max(safeMinY, topMaxY)
    });
    
    // Área inferior (evita logo central)
    if (forbidden) {
      areas.push({ 
        minX: safeMinX, 
        maxX: safeMaxX, 
        minY: Math.min(safeMaxY, forbidden.bottom), 
        maxY: safeMaxY 
      });
    }
    
    // Área inferior esquerda (evita logo ACHE)
    if (forbiddenAche) {
      areas.push({ 
        minX: safeMinX, 
        maxX: Math.max(safeMinX, forbiddenAche.left - balloonWidth), 
        minY: Math.min(safeMaxY, forbiddenAche.bottom), 
        maxY: safeMaxY 
      });
    }

    const validAreas = areas.filter(area => area.maxX > area.minX && area.maxY > area.minY);

    if (validAreas.length > 0) {
      const area = validAreas[Math.floor(Math.random() * validAreas.length)];
      left = Math.random() * (area.maxX - area.minX) + area.minX;
      top = Math.random() * (area.maxY - area.minY) + area.minY;
    }
  }

  return { left, top };
};

// Função para gerar posição de entrada aleatória (de fora da tela)
const getEntryPosition = (balloonSize: number = 50) => {
  const side = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
  
  switch (side) {
    case 0: // Entrada pelo topo
      return {
        left: Math.random() * (window.innerWidth - balloonSize),
        top: -balloonSize - 100
      };
    case 1: // Entrada pela direita
      return {
        left: window.innerWidth + 100,
        top: Math.random() * (window.innerHeight - balloonSize)
      };
    case 2: // Entrada por baixo
      return {
        left: Math.random() * (window.innerWidth - balloonSize),
        top: window.innerHeight + 100
      };
    case 3: // Entrada pela esquerda
    default:
      return {
        left: -balloonSize - 100,
        top: Math.random() * (window.innerHeight - balloonSize)
      };
  }
};

// Função para determinar posição de saída mais próxima
const getExitTarget = (currentPos: { left: number; top: number }, balloonSize: number = 50) => {
  const { left, top } = currentPos;
  
  // Calcular distâncias até cada borda
  const distToLeft = left;
  const distToTop = top;
  const distToRight = window.innerWidth - left - balloonSize;
  const distToBottom = window.innerHeight - top - balloonSize;
  
  // Encontrar a borda mais próxima
  const minDist = Math.min(distToLeft, distToTop, distToRight, distToBottom);
  
  // Gerar posição de saída pela borda mais próxima
  if (minDist === distToLeft) {
    return { left: -balloonSize - 100, top }; // Sair pela esquerda
  } else if (minDist === distToTop) {
    return { left, top: -balloonSize - 100 }; // Sair pelo topo
  } else if (minDist === distToRight) {
    return { left: window.innerWidth + 100, top }; // Sair pela direita
  } else {
    return { left, top: window.innerHeight + 100 }; // Sair por baixo
  }
};

const FloatingAcheBalloon: React.FC<FloatingAcheBalloonProps> = ({
  id,
  speed = 10,
  minDistance = 2,
  initialPosition,
  movementBounds,
  sizePercentage = 5,
  className = "",
  style = {},
  shouldEnter = false,
  shouldExit = false,
  onExitComplete
}) => {
  // Calcular tamanho do balão
  const balloonSize = Math.max(40, window.innerHeight * (sizePercentage / 100));
  
  // Determinar posição inicial baseada em shouldEnter
  const getInitialPosition = () => {
    if (initialPosition) return initialPosition;
    if (shouldEnter) return getEntryPosition(balloonSize);
    return getRandomTarget(movementBounds, balloonSize);
  };
  
  const [position, setPosition] = useState<{ left: number; top: number }>(getInitialPosition());
  const targetRef = useRef<{ left: number; top: number }>(getRandomTarget(movementBounds, balloonSize));
  const animationFrameRef = useRef<number>();
  const lastUpdateRef = useRef<number>(Date.now());
  const isExitingRef = useRef<boolean>(false);
  const hasEnteredRef = useRef<boolean>(!shouldEnter); // Se não deve entrar, já está "dentro"
  const hasExitedRef = useRef<boolean>(false); // Para evitar múltiplas chamadas do onExitComplete

  // Atualizar alvo quando shouldExit muda
  useEffect(() => {
    if (shouldExit && !isExitingRef.current) {
      isExitingRef.current = true;
      targetRef.current = getExitTarget(position, balloonSize);
    }
  }, [shouldExit, position, balloonSize]);

  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      const dt = Math.min((now - lastUpdateRef.current) / 1000, 0.05); // segundos
      lastUpdateRef.current = now;

      setPosition(prevPos => {
        let target = targetRef.current;
        let dx = target.left - prevPos.left;
        let dy = target.top - prevPos.top;
        let dist = Math.sqrt(dx * dx + dy * dy);

        // Se está saindo e chegou perto do alvo de saída
        if (isExitingRef.current && dist < minDistance * 3) {
          if (onExitComplete && !hasExitedRef.current) {
            hasExitedRef.current = true;
            onExitComplete();
          }
          return prevPos; // Parar movimento
        }

        // Se chegou perto do alvo, gere um novo alvo imediatamente
        if (dist < minDistance) {
          // Se ainda não entrou completamente na tela, continuar entrando
          if (!hasEnteredRef.current) {
            const isInScreen = (
              prevPos.left >= 0 &&
              prevPos.left <= window.innerWidth - balloonSize &&
              prevPos.top >= 0 &&
              prevPos.top <= window.innerHeight - balloonSize
            );
            
            if (isInScreen) {
              hasEnteredRef.current = true;
              target = getRandomTarget(movementBounds, balloonSize);
            } else {
              // Continuar entrando na tela
              target = getRandomTarget(movementBounds, balloonSize);
            }
          } else if (!isExitingRef.current) {
            // Movimento normal dentro da tela
            target = getRandomTarget(movementBounds, balloonSize);
          }
          
          targetRef.current = target;
          dx = target.left - prevPos.left;
          dy = target.top - prevPos.top;
          dist = Math.sqrt(dx * dx + dy * dy);
        }

        // Evitar divisão por zero
        if (dist === 0) return prevPos;

        // Movimento com velocidade constante (igual ao FloatingPhotoBubble)
        let moveDist = speed * dt;
        if (moveDist > dist) moveDist = dist;
        
        const newLeft = prevPos.left + (dx / dist) * moveDist;
        const newTop = prevPos.top + (dy / dist) * moveDist;

        // Se está saindo, permitir sair da tela
        if (isExitingRef.current) {
          return { left: newLeft, top: newTop };
        }

        // Se ainda está entrando, permitir movimento fora da tela até entrar
        if (!hasEnteredRef.current) {
          return { left: newLeft, top: newTop };
        }

        // Garantir que o balão não saia da tela durante movimento normal
        let clampedLeft = Math.max(0, Math.min(window.innerWidth - balloonSize, newLeft));
        let clampedTop = Math.max(0, Math.min(window.innerHeight - balloonSize, newTop));

        // Verificar se a nova posição está na área proibida (igual ao FloatingPhotoBubble)
        const isInForbiddenArea = (x: number, y: number) => {
          if (!movementBounds?.forbiddenArea && !movementBounds?.forbiddenAreaAche && !movementBounds?.forbiddenAreaText) return false;
          
          // Verificar área proibida principal (logo ASCME central)
          if (movementBounds?.forbiddenArea) {
            const forbidden = movementBounds.forbiddenArea;
            const inMainForbiddenArea = (
              x + balloonSize > forbidden.left &&
              x < forbidden.right &&
              y + balloonSize > forbidden.top &&
              y < forbidden.bottom
            );
            if (inMainForbiddenArea) return true;
          }
          
          // Verificar área proibida adicional (logo ACHE canto)
          if (movementBounds?.forbiddenAreaAche) {
            const forbiddenAche = movementBounds.forbiddenAreaAche;
            const inAcheForbiddenArea = (
              x + balloonSize > forbiddenAche.left &&
              x < forbiddenAche.right &&
              y + balloonSize > forbiddenAche.top &&
              y < forbiddenAche.bottom
            );
            if (inAcheForbiddenArea) return true;
          }
          
          // Verificar área proibida do texto central
          if (movementBounds?.forbiddenAreaText) {
            const forbiddenText = movementBounds.forbiddenAreaText;
            const inTextForbiddenArea = (
              x + balloonSize > forbiddenText.left &&
              x < forbiddenText.right &&
              y + balloonSize > forbiddenText.top &&
              y < forbiddenText.bottom
            );
            if (inTextForbiddenArea) return true;
          }
          
          return false;
        };

        // Se está entrando na área proibida, gerar novo alvo
        if (isInForbiddenArea(clampedLeft, clampedTop)) {
          target = getRandomTarget(movementBounds, balloonSize);
          targetRef.current = target;
          return prevPos; // Manter posição atual
        }

        return { left: clampedLeft, top: clampedTop };
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [speed, minDistance, movementBounds, balloonSize, onExitComplete]);

  return (
    <div
      className={`ache-balloon ${className}`}
      style={{
        position: "absolute",
        left: position.left,
        top: position.top,
        width: balloonSize,
        height: balloonSize,
        zIndex: 2,
        pointerEvents: "none",
        ...style
      }}
    >
      <img 
        src={getImageUrl('balão_ache_01.png')} 
        alt="ACHE Balloon"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain"
        }}
      />
    </div>
  );
};

export default FloatingAcheBalloon;